import { useEffect, useState } from "react";
import { log } from "@connect/observability";
import type { SelectOption } from "@connect/component-library/components/select";
import type { InternalClient } from "@connect/data/internal/client";

// Fetches the country list once and exposes it as sorted Select options. On any
// failure the options stay empty; the billing draft still seeds a country, so
// submit degrades to the configured/fallback country.
export function useCountries(client: InternalClient) {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    const fail = (reason: string) => {
      if (!active) {
        return;
      }
      log.warn("failed to load countries", { reason });
      setError(true);
      setLoading(false);
    };

    client
      .GET("/v3/countries")
      .then((result) => {
        if (!active) {
          return;
        }
        // The schema declares only a 200, so openapi-fetch types `error` as
        // never; a non-ok response surfaces here as missing data.
        if (!result.data) {
          fail("empty response");
          return;
        }
        const mapped = result.data
          .map((c) => ({ value: c.alpha3, label: c.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setOptions(mapped);
        setLoading(false);
      })
      .catch((err) => fail(String(err)));

    return () => {
      active = false;
    };
  }, [client]);

  return { options, loading, error };
}
